
import { useState, useEffect } from "react";
import PageTransition from "@/components/PageTransition";
import { useAuth } from "@/context/AuthContext";
import PersonaDashboard from "@/components/dashboard/PersonaDashboard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Activity, BarChart2, PieChartIcon, RefreshCw, Loader2, Home, Store, Building2, Building } from "lucide-react";
import { useDashboardData } from "@/hooks/useDashboardData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useInventoryItems } from "@/hooks/useInventoryItems";
import OverviewTab from "@/components/dashboard/tabs/OverviewTab";
import AnalyticsTab from "@/components/dashboard/tabs/AnalyticsTab";
import InventoryTab from "@/components/dashboard/tabs/InventoryTab";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const Dashboard = () => {
  const { user } = useAuth();
  const [timeFrame, setTimeFrame] = useState<string>("7");
  const [userPersona, setUserPersona] = useState<string | null>(null);
  const [isLoadingPersona, setIsLoadingPersona] = useState(true);
  
  const { 
    stats, 
    categoryDistribution, 
    expiryTrend, 
    inventoryHealth,
    locationDistribution,
    totals,
    percentages,
    isLoading,
    hasErrors,
    refetchAll
  } = useDashboardData(parseInt(timeFrame));

  // Get inventory items for the table
  const { items, isLoading: itemsLoading, refreshItems } = useInventoryItems();
  
  useEffect(() => {
    const fetchUserPersona = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('persona')
          .eq('id', user.id)
          .maybeSingle();
          
        if (error) throw error;
        setUserPersona(data?.persona || null);
      } catch (error) {
        console.error("Error fetching user persona:", error);
      } finally {
        setIsLoadingPersona(false);
      }
    };
    
    fetchUserPersona();
  }, [user]);
  
  const handleRefresh = () => {
    refetchAll();
    refreshItems();
  };
  
  // Get persona display name and icon
  const getPersonaInfo = () => {
    switch(userPersona) {
      case 'household':
        return { name: 'Household', icon: <Home className="h-4 w-4" /> };
      case 'restaurant':
        return { name: 'Restaurant', icon: <Store className="h-4 w-4" /> };
      case 'retail':
        return { name: 'Retail', icon: <Building className="h-4 w-4" /> };
      case 'healthcare':
        return { name: 'Healthcare', icon: <Building2 className="h-4 w-4" /> };
      default:
        return { name: 'Standard', icon: <Home className="h-4 w-4" /> };
    }
  };
  
  const personaInfo = getPersonaInfo();

  return (
    <PageTransition className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
          <div className="flex flex-col">
            <h1 className="text-2xl font-bold">Your Dashboard</h1>
            
            {!isLoadingPersona && (
              <div className="flex items-center mt-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="flex items-center gap-1 px-2 py-1 bg-slate-50">
                        {personaInfo.icon}
                        <span className="ml-1">{personaInfo.name} Dashboard</span>
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Current dashboard type</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Alert window:</span>
              <Select value={timeFrame} onValueChange={setTimeFrame}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Select days" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh} 
              className="flex items-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Refresh
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="overview" className="mb-8">
          <TabsList className="mb-4">
            <TabsTrigger value="overview" className="flex items-center gap-1">
              <Activity className="h-4 w-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-1">
              <BarChart2 className="h-4 w-4" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="inventory" className="flex items-center gap-1">
              <PieChartIcon className="h-4 w-4" /> Inventory
            </TabsTrigger>
          </TabsList>
          
          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <OverviewTab 
              stats={stats}
              locationDistribution={locationDistribution}
              items={items}
              expiryTrend={expiryTrend}
              percentages={percentages}
              isLoading={isLoading || itemsLoading}
              timeFrame={timeFrame}
            />
          </TabsContent>
          
          {/* ANALYTICS TAB */}
          <TabsContent value="analytics">
            <AnalyticsTab 
              categoryDistribution={categoryDistribution}
              expiryTrend={expiryTrend}
              inventoryHealth={inventoryHealth}
              isLoading={isLoading}
            />
          </TabsContent>
          
          {/* INVENTORY TAB */}
          <TabsContent value="inventory">
            <InventoryTab 
              stats={stats}
              expiryTrend={expiryTrend}
              percentages={percentages}
              isLoading={isLoading || itemsLoading}
              categoryDistribution={categoryDistribution}
              items={items}
            />
          </TabsContent>
        </Tabs>
      </div>
    </PageTransition>
  );
};

export default Dashboard;
